// src/components/sla/ai/SlaHotJobsTable.js
// Jobs breaching or at risk right now (from /sla-ai/overview), with projected grade and a
// "What will the AI do?" preview per row.
import React, { memo, useCallback, useState } from 'react';
import {
    Badge, Box, Button, HStack, Icon, Progress, Table, Tbody, Td, Text, Th, Thead, Tr, useColorModeValue, useDisclosure,
} from '@chakra-ui/react';
import { MdLocalFireDepartment } from 'react-icons/md';
import SlaGradeBadge from './SlaGradeBadge';
import SlaWorkOrderPreviewModal from './SlaWorkOrderPreviewModal';
import { fmtMinutes } from './slaAiFormat';

const PRIORITY_SCHEME = { critical: 'red', urgent: 'red', high: 'orange', medium: 'yellow', low: 'gray' };

function SlaHotJobsTable({ jobs = [], loading, onChanged }) {
    const [woId, setWoId] = useState(null);
    const modal = useDisclosure();
    const muted = useColorModeValue('gray.500', 'gray.400');
    const open = useCallback((id) => { setWoId(id); modal.onOpen(); }, [modal]);

    return (
        <Box>
            <HStack mb={3}><Icon as={MdLocalFireDepartment} color="red.400" /><Text fontWeight="800">Breaching &amp; at risk now</Text><Badge colorScheme="red">{jobs.length}</Badge></HStack>
            {!loading && jobs.length === 0 ? (
                <Text fontSize="sm" color={muted} py={4}>Every open job is inside its SLA window.</Text>
            ) : (
                <Box overflowX="auto" borderWidth="1px" borderRadius="xl">
                    <Table size="sm">
                        <Thead><Tr><Th>Work order</Th><Th>Priority</Th><Th>Assignee</Th><Th>SLA</Th><Th>Projected</Th><Th isNumeric>Level</Th><Th /></Tr></Thead>
                        <Tbody>
                            {jobs.map(j => (
                                <Tr key={j.id}>
                                    <Td maxW="260px"><Text fontWeight="700" fontSize="sm" noOfLines={1}>#{j.id} {j.title}</Text><Text fontSize="xs" color={muted}>{String(j.status || '').replace(/_/g, ' ')}</Text></Td>
                                    <Td><Badge colorScheme={PRIORITY_SCHEME[String(j.priority).toLowerCase()] || 'gray'}>{j.priority || '—'}</Badge></Td>
                                    <Td fontSize="sm">{j.assignee || <Text as="span" color="red.400">Unassigned</Text>}</Td>
                                    <Td minW="140px">
                                        {j.minutes_late > 0
                                            ? <Badge colorScheme="red">{fmtMinutes(j.minutes_late)} late</Badge>
                                            : <Box><Progress value={Math.min(100, j.elapsed_pct)} size="xs" colorScheme="orange" borderRadius="full" /><Text fontSize="2xs" color={muted}>{Math.round(j.elapsed_pct)}% used</Text></Box>}
                                    </Td>
                                    <Td><SlaGradeBadge grade={j.projected_grade} score={j.projected_score} size="sm" projected /></Td>
                                    <Td isNumeric>{j.escalation_level || 0}</Td>
                                    <Td><Button size="xs" colorScheme="purple" variant="outline" onClick={() => open(j.id)}>AI preview</Button></Td>
                                </Tr>
                            ))}
                        </Tbody>
                    </Table>
                </Box>
            )}
            <SlaWorkOrderPreviewModal workOrderId={woId} isOpen={modal.isOpen} onClose={modal.onClose} onDone={onChanged} />
        </Box>
    );
}

export default memo(SlaHotJobsTable);
